import { useQuestionBuilder } from 'lib-node-question-builder';
import { and, formIs, or, screenIs } from 'lib-node-question-builder/utils';

import Button from '~/common/components/Button';
import ConfirmationDialog from '~/common/components/ConfirmationDialog';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import useUrl from '~/common/hooks/useUrl';
import Loan1003FormContainer from '~/pages/Loan1003DataGrids/common/components/Loan1003FormContainer';
import Loan1003FormSection from '~/pages/Loan1003DataGrids/common/components/Loan1003FormSection';

import {
  NEW_SUBORDINATE_FINANCING_DROPDOWN,
  READONLY_CONDITION,
} from '../constants';
import {
  useSaveSubordinateFinancingMutation,
  useSubordinateFinancingQuery,
} from '../hooks';
import { useAsset1003Store } from '../store';

const COMMUNITY_SECOND_OPTIONS_WITHOUT_EMPTY =
  NEW_SUBORDINATE_FINANCING_DROPDOWN.filter((option) => option.value !== 0);

export function SubordinateFinancingForm() {
  const { isLoading, mutate } = useSaveSubordinateFinancingMutation();

  const { data } = useSubordinateFinancingQuery();

  const { isReadOnly } = useUrl();

  const isWriteAccessDisabled = useIsWriteAccessDisabled() || isReadOnly;
  const loanSummary = useGetLoanSummary();
  const isDesktopUnderwriter =
    loanSummary.data?.loanInfo?.lastAusRunMethod === 'Desktop Underwriter';

  const { form, handleSubmit } = useQuestionBuilder({
    formData: data,
    submitMeta: {
      deleteSubordinateFinancing: false,
    },
    screenData: {
      isWriteAccessDisabled,
      isDesktopUnderwriter,
    },
    hooks: {
      onSubmit: (values, _, meta) => {
        const noteRate = Number(values.noteRate ?? 0);

        mutate({
          ...values,
          noteRate,
          deleteSubordinateFinancing: meta.deleteSubordinateFinancing,
        });
      },
    },
  });

  return (
    <form.Provider>
      <Loan1003FormContainer
        isLoading={isLoading || !data}
        readOnlyMode={isWriteAccessDisabled}
        onSaveClicked={() => {
          handleSubmit();
        }}
        onCloseClicked={() => {
          useAsset1003Store.setState({ activeForm: null });
        }}
        extraActionButton={
          <ConfirmationDialog
            title="Remove New Subordinate Financing"
            message="Are you sure you want to remove the new subordinate financing?"
            isDeleteDisabled={false}
            onClick={() => {
              handleSubmit({ deleteSubordinateFinancing: true });
            }}
          >
            <Button isPrimary={false}>Remove New Subordinate Financing</Button>
          </ConfirmationDialog>
        }
      >
        <Loan1003FormSection
          title="Add New Subordinate Financing"
          isAuditHistoryEnabled
        >
          <form.Grid name="add new subordinate financing grid" columns={6}>
            <form.GridItem colSpan={2}>
              <form.Text
                name="creditorName"
                label="Creditor Name"
                rules={{ required: true }}
                conditions={{ isDisabled: READONLY_CONDITION }}
              />
            </form.GridItem>
            <form.GridItem colSpan={2}>
              <form.Percentage
                name="noteRate"
                label="Note Rate"
                conditions={{ isDisabled: READONLY_CONDITION }}
                listeners={{
                  onChange: ({ value, fieldApi }) => {
                    const noteRate = value as string;

                    if (noteRate && !String(noteRate).includes('.')) {
                      fieldApi.handleChange(`${noteRate}.000` as any);
                    }
                  },
                }}
              />
            </form.GridItem>
            <form.GridItem colSpan={2}>
              <form.Number
                name="amortizingTerm"
                label="Amortizing Term"
                conditions={{ isDisabled: READONLY_CONDITION }}
              />
            </form.GridItem>
            <form.GridItem colSpan={3}>
              <form.Number
                name="numberOfPayments"
                label="Number of Payments"
                conditions={{ isDisabled: READONLY_CONDITION }}
              />
            </form.GridItem>
            <form.GridItem colSpan={3}>
              <form.Number
                name="paymentsPerYear"
                label="Payments Per Year"
                rules={{
                  required: true,
                  onBlur: {
                    type: 'number',
                    gt: 0,
                    message: 'Required',
                  },
                }}
                conditions={{ isDisabled: READONLY_CONDITION }}
              />
            </form.GridItem>
            <form.GridItem colSpan={3}>
              <form.Currency
                name="subordinateFinancingAmount"
                label="New Subordinate Financing Amount"
                rules={{
                  required: true,
                  onBlur: {
                    type: 'number',
                    gt: 0,
                    message: 'Required',
                  },
                }}
                conditions={{
                  isDisabled: or(
                    READONLY_CONDITION,
                    formIs(
                      'isHomeTownHeroSubordinateFinancingFieldReadOnly',
                      true,
                    ),
                  ),
                }}
              />
            </form.GridItem>
            <form.GridItem colSpan={3}>
              <form.Currency
                name="paymentAmount"
                label="Payment Amount"
                conditions={{
                  isDisabled: true,
                }}
              />
            </form.GridItem>
          </form.Grid>
        </Loan1003FormSection>
        <Loan1003FormSection title="Additional Actions">
          <form.Checkbox
            name="isCommunitySecond"
            label="Community Second"
            hasBottomBorder={false}
            style={{ padding: '0 0 0 0.5rem' }}
            conditions={{
              isDisabled: READONLY_CONDITION,
            }}
          />
          <form.Subscribe
            selector={(state) => state.values.selectedCommunitySecondOption}
          >
            {(selectedCommunitySecondOption) => {
              const hasSelectedCommunitySecondOption =
                selectedCommunitySecondOption != null &&
                Number(selectedCommunitySecondOption) !== 0;

              const communitySecondOptions = hasSelectedCommunitySecondOption
                ? COMMUNITY_SECOND_OPTIONS_WITHOUT_EMPTY
                : NEW_SUBORDINATE_FINANCING_DROPDOWN;

              return (
                <form.Grid
                  name="community second grid"
                  columns={2}
                  style={{ padding: '0 0.5rem 0.5rem 1rem' }}
                  conditions={{
                    isVisible: and(
                      formIs('isCommunitySecond', true),
                      screenIs('isDesktopUnderwriter', true),
                    ),
                    isDisabled: READONLY_CONDITION,
                  }}
                >
                  <form.GridItem>
                    <form.Select
                      name="selectedCommunitySecondOption"
                      label="Select Type of Community Second"
                      options={communitySecondOptions}
                      conditions={{
                        isDisabled: READONLY_CONDITION,
                      }}
                    />
                  </form.GridItem>
                </form.Grid>
              );
            }}
          </form.Subscribe>
          <form.Checkbox
            name="helocFlag"
            label="HELOC Flag"
            hasBottomBorder={false}
            style={{ padding: '0 0 0 0.5rem' }}
            conditions={{
              isDisabled: READONLY_CONDITION,
            }}
          />
          <form.Grid
            name="community second grid"
            columns={2}
            style={{ padding: '0 0.5rem 0.5rem 1rem' }}
            conditions={{
              isVisible: formIs('helocFlag', true),
              isDisabled: READONLY_CONDITION,
            }}
          >
            <form.GridItem>
              <form.Currency
                name="highestBalance"
                label="Highest Balance"
                conditions={{
                  isDisabled: READONLY_CONDITION,
                }}
              />
            </form.GridItem>
            <form.GridItem>
              <form.Currency
                name="undrawnAmount"
                label="Undrawn Amount"
                conditions={{
                  isDisabled: true,
                }}
              />
            </form.GridItem>
          </form.Grid>
          <form.Checkbox
            name="interestOnly"
            label="Interest Only"
            hasBottomBorder={false}
            style={{ padding: '0 0 0 0.5rem' }}
            conditions={{
              isDisabled: READONLY_CONDITION,
            }}
          />
        </Loan1003FormSection>
      </Loan1003FormContainer>
    </form.Provider>
  );
}
